/**
 * Datenqualität: was die Engine nicht bewerten konnte, gruppiert nach Art.
 *
 * Stilles Wegfallen war der teuerste Einzelbefund (Nr. 7 und 8) — deshalb hat
 * jede Luecke hier eine Zeile mit Projektnamen.
 */
import React, { useMemo } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '../Button';
import type { CockpitWarning } from '../../services/cockpitTypes';
import { ModalShell } from './CockpitBits';
import { WARNING_LABEL } from './cockpitUi';

export const WarningsModal: React.FC<{ warnings: CockpitWarning[]; onClose: () => void }> = ({ warnings, onClose }) => {
    const groups = useMemo(() => {
        const map = new Map<string, CockpitWarning[]>();
        warnings.forEach((w) => {
            const list = map.get(w.kind) ?? [];
            list.push(w);
            map.set(w.kind, list);
        });
        return [...map.entries()].sort((a, b) => b[1].length - a[1].length);
    }, [warnings]);

    return (
        <ModalShell
            title={`${warnings.length} Hinweise zur Datenqualität`}
            width="max-w-2xl"
            onClose={onClose}
            footer={<><span className="text-[11px] text-slate-400">{groups.length} Arten</span><Button variant="primary" size="sm" onClick={onClose}>Schließen</Button></>}
        >
            {groups.map(([kind, list]) => (
                <section key={kind}>
                    <h4 className="text-xs font-semibold text-slate-700 uppercase mb-2 flex items-center gap-2">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                        {WARNING_LABEL[kind] ?? kind}
                        <span className="text-[11px] font-normal text-slate-400">({list.length})</span>
                    </h4>
                    <div className="space-y-1">
                        {list.map((w, i) => (
                            <div key={`${kind}-${w.projectId ?? i}-${i}`} className="p-2.5 rounded-lg border border-amber-200 bg-amber-50">
                                {w.projectName && <div className="text-[11px] font-semibold text-slate-700">{w.projectName}</div>}
                                <div className="text-[11px] text-slate-600">{w.text}</div>
                                {(w.count !== undefined || w.cents !== undefined) && (
                                    <div className="text-[11px] text-slate-400 mt-0.5">
                                        {w.count !== undefined ? `${w.count} Einträge` : ''}
                                        {w.count !== undefined && w.cents !== undefined ? ' · ' : ''}
                                        {w.cents !== undefined ? `${Math.round(w.cents / 100).toLocaleString('de-DE')} €` : ''}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </section>
            ))}
        </ModalShell>
    );
};
